import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonIconComponent,
	CardAccessComponent,
	DetailLayoutComponent,
	IMenu,
	ItemComponent,
	ListDetailComponent,
	MenuComponent,
	StatsComponent,
	TagGroupComponent,
	ISelect,
	IBackEvent,
} from '@tecalisdev/infinity';
import { MenuItemAction } from '../../../shared/models/master.models';
import { LoadingService } from '../../../shared/services/loading.service';
import { ModalConfirmationService } from '../../../shared/services/modal-confirmation.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { ModalWorkflowFormComponent } from '../../components/modal-workflow-form/modal-workflow-form.component';
import { ModalWorkflowJsonEditorComponent } from '../../components/modal-workflow-json-editor/modal-workflow-json-editor.component';
import { IWorkflowDetailResponse } from '../../models/workflows.models';
import { WorkflowsHelperService } from '../../services/workflows-helper.service';
import { WorkflowsService } from '../../services/workflows.service';
import { WorkflowsDetailJsonViewComponent } from './workflows-detail-json-view/workflows-detail-json-view.component';
import { WorkflowsDetailViewComponent } from './workflows-detail-view/workflows-detail-view.component';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { UuidFlexibleDisplayComponent } from '../../../shared/components/uuid-flexible-display/uuid-flexible-display.component';
import { ModalCreateProcessComponent } from '../../../processes/components/modal-create-process/modal-create-process.component';
import { NavigationService } from '../../../shared/services/navigation.service';
import { UserActivityTrackService } from '../../../core/services/user-activity-track.service';
import { DetailResourceErrorComponent } from '../../../shared/components/detail-resource-error/detail-resource-error.component';
import {
	DetailResourceErrorView,
	resolveDetailResourceError,
} from '../../../core/utils/http-resource-error.utils';
import { Permission } from '../../../core/models/role-permissions.models';
import { environment } from '../../../../environments/environment';
import { buildCurlGet } from '../../../panel/utils/build-curl.util';
import { ModalCurlRequestComponent } from '../../../shared/components/modal-curl-request/modal-curl-request.component';

@Component({
	selector: 'app-workflows-detail',
	imports: [
		TranslatePipe,
		ItemComponent,
		ListDetailComponent,
		TagGroupComponent,
		MenuComponent,
		ButtonIconComponent,
		CardAccessComponent,
		ModalWorkflowFormComponent,
		ModalWorkflowJsonEditorComponent,
		StatsComponent,
		WorkflowsDetailJsonViewComponent,
		WorkflowsDetailViewComponent,
		DetailLayoutComponent,
		UuidFlexibleDisplayComponent,
		ModalCreateProcessComponent,
		DetailResourceErrorComponent,
		ModalCurlRequestComponent,
	],
	templateUrl: './workflows-detail.component.html',
	styleUrl: './workflows-detail.component.scss',
})
export class WorkflowsDetailComponent {
	public workflowsHelper = inject(WorkflowsHelperService);
	private workflowsService = inject(WorkflowsService);
	private loading = inject(LoadingService);
	private activatedRoute = inject(ActivatedRoute);
	private router = inject(Router);
	private navigationService = inject(NavigationService);
	private trans = inject(TranslateService);
	private snackbarService = inject(SnackbarService);
	private modalConfirmationService = inject(ModalConfirmationService);
	public rolePermissions = inject(RolePermissionsService);
	private userActivityTrack = inject(UserActivityTrackService);

	public detail?: IWorkflowDetailResponse;
	private id?: string;
	public errorDetail: string;
	public onlyJson: boolean = false;
	public showModalEdit = false;
	public showModalJsonEdit = false;
	public canEditModalJsonEdit = false;
	public error: HttpErrorResponse | null = null;
	public resourceError: DetailResourceErrorView | null = null;
	public workflow: ISelect;

	public readonly curlModalOpen = signal(false);
	public readonly curlModalTitleKey = signal('WORKFLOWS.CURL_MODAL.TITLE');
	public readonly curlModalText = signal('');

	constructor() {
		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			if (modalResponse?.id?.menuAction === MenuItemAction.DELETE) {
				if (modalResponse?.action === 'buttonPrimary' && this.rolePermissions.canDeleteWorkflow()) {
					this.deleteWorkflow(this.id);
				}
			}
		});
	}

	ngOnInit(): void {
		this.id = this.activatedRoute.snapshot.params['id'];
		if (this.id) this.getDetail();
	}

	public canViewDeveloperCurl(): boolean {
		return this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION);
	}

	public openCurlModal(titleKey: string, path: string): void {
		this.curlModalTitleKey.set(titleKey);
		this.curlModalText.set(buildCurlGet(environment.apiUrl, path));
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public profileCurlPath(suffix = ''): string {
		const uuid = this.detail?.uuid || this.id || '';
		return `/profile/${uuid}${suffix}`;
	}

	public getDetail(): void {
		this.errorDetail = '';
		this.error = null;
		this.resourceError = null;
		this.loading.show(this.trans.instant('LOADING.GET_WORKFLOWS'));
		this.workflowsService.detail(this.id).subscribe({
			next: (response: IWorkflowDetailResponse) => {
				this.detail = response;
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
				this.errorDetail = error.error.message || this.trans.instant('WORKFLOWS.FORM.DETAIL_ERROR_DESCRIPTION');
				this.error = error;
				this.resourceError = resolveDetailResourceError(error);
				this.detail = undefined;
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public dataMenuChange(action: IMenu): void {
		const actions = {
			[MenuItemAction.STATUS]: () => this.toggleStatus(this.id),
			[MenuItemAction.EDIT]: () => this.goToEdit(this.id, action.event),
			[MenuItemAction.DELETE]: () => this.openDelete(this.id),
		};

		actions[action.id]() || null;
	}

	public dataJsonMenuChange(action: IMenu): void {
		const actions = {
			[MenuItemAction.SHOW]: () => this.goToSeeJson(this.id),
			[MenuItemAction.EDIT]: () => this.goToEditJson(this.id),
			[MenuItemAction.DOWNLOAD]: () => this.downloadJson(this.id),
		};

		actions[action.id]() || null;
	}

	private toggleStatus(id?: string): void {
		if (!id || !this.detail) return;

		const newStatus = this.detail.status === 'active' ? 'inactive' : 'active';

		this.loading.show(this.trans.instant('LOADING.UPDATE_WORKFLOW_STATUS'));
		this.workflowsService.toggleStatus(id, newStatus).subscribe({
			next: () => {
				if (this.detail) {
					this.detail.status = newStatus;
					this.snackbarService.show(
						this.trans.instant(
							newStatus === 'active'
								? 'WORKFLOWS.DETAIL.STATUS_ACTIVATED'
								: 'WORKFLOWS.DETAIL.STATUS_DEACTIVATED',
						),
					);
				}
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
				this.snackbarService.show(error.error?.message || this.trans.instant('WORKFLOWS.DETAIL.STATUS_ERROR'));
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	private goToEdit(id?: string, event?: Event): void {
		if (!id) return;

		if (this.rolePermissions.canViewWorkflowJson()) {
			this.showModalEdit = true;
			this.canEditModalJsonEdit = true;
		} else {
			this.navigationService.navigateTo({ routeSegments: ['workflows', 'edit', id] }, event);
		}
	}

	private openDelete(id?: string): void {
		if (!id || !this.rolePermissions.canDeleteWorkflow()) return;

		this.modalConfirmationService.show({
			title: this.trans.instant('WORKFLOWS.LIST.MODAL_DELETE_TITLE'),
			description: this.trans.instant('WORKFLOWS.LIST.MODAL_DELETE_DESCRIPTION'),
			buttonPrimaryLabel: this.trans.instant('BUTTON.DELETE'),
			buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
			buttonPrimaryStyle: true,
			id: { menuAction: MenuItemAction.DELETE },
		});
	}

	private goToSeeJson(id?: string): void {
		if (!id || !this.rolePermissions.canViewWorkflowJson()) return;

		this.showModalJsonEdit = true;
		this.canEditModalJsonEdit = false;
	}

	private goToEditJson(id?: string): void {
		if (!id || !this.rolePermissions.canViewWorkflowJson()) return;

		this.showModalJsonEdit = true;
		this.canEditModalJsonEdit = true;
	}

	private downloadJson(id?: string): void {
		if (!id) return;
		this.loading.show(this.trans.instant('LOADING.GET_WORKFLOWS'));
		this.workflowsService.detail(id).subscribe({
			next: (response: IWorkflowDetailResponse) => {
				try {
					const nameBase = (response.alias || id || 'workflow').replace(/\s+/g, '-').toLowerCase();
					const filename = `workflow-${nameBase}.json`;
					const content = JSON.stringify(response, null, 2);
					const blob = new Blob([content], { type: 'application/json' });
					const url = URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = filename;
					document.body.appendChild(a);
					a.click();
					a.remove();
					URL.revokeObjectURL(url);
					this.userActivityTrack.trackClientDownload({
						type: 'workflows',
						action: 'downloaded_json',
						link: `/settings/workflows/detail/${id}`,
						company_uuid: response.company_uuid,
					});
					this.snackbarService.show(this.trans.instant('WORKFLOWS.DETAIL.DOWNLOAD_SUCCESS'));
				} catch (error) {
					console.error('Error exporting JSON:', error);
					this.snackbarService.show(this.trans.instant('WORKFLOWS.DETAIL.DOWNLOAD_ERROR'));
				}
			},
			error: (error: HttpErrorResponse) => {
				console.error('Error fetching workflow:', error);
				this.snackbarService.show(this.trans.instant('WORKFLOWS.DETAIL.DOWNLOAD_ERROR'));
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public closeModalEdit({
		eventType = undefined,
		event,
	}: {
		eventType: undefined | 'assisted' | 'json' | 'default';
		event?: Event;
	}): void {
		this.showModalEdit = false;
		if (eventType === 'assisted') {
			if (!this.id) return;
			this.navigationService.navigateTo({ routeSegments: ['workflows', 'edit', this.id] }, event);
		} else if (eventType === 'json') {
			this.showModalJsonEdit = true;
			this.canEditModalJsonEdit = true;
		}
	}

	public closeModalJsonEdit(reload: boolean): void {
		if (reload) this.getDetail();
		this.showModalJsonEdit = false;
		this.canEditModalJsonEdit = false;
	}

	public openGenerateOneTimeLink(): void {
		this.workflow = {
			id: this.detail.uuid,
			label: this.detail.alias,
			value: this.detail,
		};
	}

	private deleteWorkflow(id: string): void {
		if (!id) return;
		this.loading.show(this.trans.instant('LOADING.DELETE_WORKFLOW'));
		this.workflowsService.delete(id).subscribe({
			next: () => {
				this.router
					.navigate(['workflows'])
					.then(() => this.snackbarService.show(this.trans.instant('WORKFLOWS.LIST.DELETE_CONFIRM')));
			},
			error: () => {
				this.loading.hide();
				this.snackbarService.show(this.trans.instant('WORKFLOWS.LIST.DELETE_ERROR'));
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public getValidationTypeOpt(detail?: IWorkflowDetailResponse): 'AUTO' | 'MANUAL' {
		const validationType = detail?.config?.validation?.type as unknown;
		if (!validationType) return 'AUTO';

		// Contrato: `{ type: 'auto'|'ok'|'ko'|'ok-ko', errors }` (a veces aplana a string)
		const processType =
			typeof validationType === 'string'
				? validationType
				: (validationType as { type?: unknown } | null | undefined)?.type;

		return processType === 'ok' || processType === 'ko' || processType === 'ok-ko' || processType === 'ok_ko'
			? 'MANUAL'
			: 'AUTO';
	}

	public goBack({ event }: IBackEvent): void {
		this.navigationService.navigateTo({ routeSegments: ['workflows'] }, event);
	}
}
